import jwtDecode from 'jwt-decode';

import { ContractError } from '../../../api';
import type { OpenedSession, PasswordGrant, SignedInUser } from '../types/domain.types';

import { loginTokenPayloadSchema, parseOrThrow, sessionOpenedSchema } from './schemas';
import type { SessionOpenedDto } from './schemas';

const decodePayload = (token: string): unknown => {
  try {
    return jwtDecode(token);
  } catch (error) {
    throw new ContractError('Unreadable session token', error);
  }
};

const userOf = (dto: SessionOpenedDto, label: string): SignedInUser => {
  const { identity } = dto;
  if (identity) {
    return {
      userId: identity.userId,
      userName: identity.userName ?? '',
      email: identity.email ?? '',
      isStaff: identity.isStaff,
      roleKey: identity.roleKey ?? null,
      adminGrant: identity.adminGrant ?? null,
    };
  }
  const payload = parseOrThrow(loginTokenPayloadSchema, decodePayload(dto.token), `${label} token`);
  return { ...payload, roleKey: null, adminGrant: null };
};

const grantOf = ({ passwordGrant, grantExpiresInSeconds }: SessionOpenedDto): PasswordGrant | null =>
  passwordGrant && typeof grantExpiresInSeconds === 'number' ? { token: passwordGrant, expiresInSeconds: grantExpiresInSeconds } : null;

export const readOpenedSession = (raw: unknown, label: string): OpenedSession => {
  const dto = parseOrThrow(sessionOpenedSchema, raw, label);
  return {
    ...userOf(dto, label),
    layout: dto.layoutInfo ?? null,
    passwordGrant: grantOf(dto),
    access: dto.access ?? null,
  };
};
